import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { hedgesAdminApi, type Hedge, type HedgeDashboard } from "@/services/api";
import {
  Search,
  MoreHorizontal,
  Shield,
  Loader2,
  Eye,
  CheckCircle,
  XCircle,
  AlertTriangle,
  TrendingUp,
  DollarSign,
  Clock,
  Settings,
  RefreshCw,
} from "lucide-react";
import { formatDate, cn } from "@/lib/utils";

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "success" | "warning" | "destructive" }> = {
  PENDING_GUARANTEE: { label: "Aguardando Garantia", variant: "warning" },
  PENDING_APPROVAL: { label: "Aguardando Aprovação", variant: "warning" },
  ACTIVE: { label: "Ativo", variant: "success" },
  PENDING_SETTLEMENT: { label: "Aguardando Liquidação", variant: "warning" },
  SETTLED: { label: "Liquidado", variant: "secondary" },
  REJECTED: { label: "Rejeitado", variant: "destructive" },
  DEFAULTED: { label: "Inadimplente", variant: "destructive" },
  CANCELLED: { label: "Cancelado", variant: "secondary" },
};

export function HedgesPage() {
  const navigate = useNavigate();
  const [hedges, setHedges] = useState<Hedge[]>([]);
  const [dashboard, setDashboard] = useState<HedgeDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [hedgesData, dashboardData] = await Promise.all([
        hedgesAdminApi.list({
          page,
          limit,
          status: statusFilter !== "all" ? statusFilter : undefined,
        }),
        hedgesAdminApi.getDashboard(),
      ]);
      setHedges(hedgesData.items);
      setTotal(hedgesData.total);
      setDashboard(dashboardData);
    } catch (error) {
      console.error("Erro ao carregar hedges:", error);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter by search locally
  const filteredHedges = hedges.filter((hedge) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      hedge.hedgeCode?.toLowerCase().includes(searchLower) ||
      hedge.invoice?.invoiceNumber?.toLowerCase().includes(searchLower) ||
      hedge.invoice?.supplierName?.toLowerCase().includes(searchLower) ||
      hedge.company?.razaoSocial?.toLowerCase().includes(searchLower) ||
      hedge.company?.cnpj?.includes(search)
    );
  });

  const totalPages = Math.ceil(total / limit);

  const formatUsdc = (value: string) => {
    const num = parseFloat(value || "0");
    return num.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    });
  };

  const formatBrl = (value: string) => {
    const num = parseFloat(value || "0");
    return num.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
    });
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Shield className="h-5 w-5 sm:h-6 sm:w-6" />}
        title="Proteção Cambial"
        description="Gerencie os hedges de importação das empresas"
        actions={
          <div className="flex gap-2">
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={loadData}
              disabled={loading}
            >
              <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
              Atualizar
            </Button>
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => navigate("/admin/hedges/settings")}
            >
              <Settings className="w-4 h-4 mr-2" />
              Configurações
            </Button>
          </div>
        }
      />

      {/* Dashboard Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Hedges Ativos
              </CardTitle>
              <Shield className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboard.activeHedgesCount}</div>
              {dashboard.pendingApprovalCount > 0 && (
                <p className="text-xs text-muted-foreground">
                  +{dashboard.pendingApprovalCount} aguardando aprovação
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Volume Total
              </CardTitle>
              <DollarSign className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatUsdc(dashboard.totalVolumeUsdc)}</div>
              <p className="text-xs text-muted-foreground">
                {formatUsdc(dashboard.totalGuaranteesUsdc)} em garantias
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Receita (Taxas)
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatBrl(dashboard.realizedRevenueBrl)}</div>
              <p className="text-xs text-muted-foreground">
                +{formatBrl(dashboard.projectedRevenueBrl)} projetado
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Em Risco
              </CardTitle>
              <AlertTriangle className={cn("h-4 w-4", dashboard.hedgesAtRiskCount > 0 ? "text-red-500" : "text-muted-foreground")} />
            </CardHeader>
            <CardContent>
              <div className={cn("text-2xl font-bold", dashboard.hedgesAtRiskCount > 0 && "text-red-500")}>
                {dashboard.hedgesAtRiskCount}
              </div>
              <p className="text-xs text-muted-foreground">
                hedges com risco elevado
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por código, invoice, fornecedor ou empresa..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="PENDING_APPROVAL">Aguardando Aprovação</SelectItem>
                <SelectItem value="ACTIVE">Ativos</SelectItem>
                <SelectItem value="PENDING_SETTLEMENT">Aguardando Liquidação</SelectItem>
                <SelectItem value="SETTLED">Liquidados</SelectItem>
                <SelectItem value="REJECTED">Rejeitados</SelectItem>
                <SelectItem value="DEFAULTED">Inadimplentes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Hedges Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-border bg-muted/30">
                <tr className="text-left text-sm text-muted-foreground">
                  <th className="p-4 font-medium">Código</th>
                  <th className="p-4 font-medium hidden md:table-cell">Empresa</th>
                  <th className="p-4 font-medium hidden lg:table-cell">Invoice</th>
                  <th className="p-4 font-medium text-right">Valor USDC</th>
                  <th className="p-4 font-medium text-right hidden sm:table-cell">Taxa Travada</th>
                  <th className="p-4 font-medium hidden sm:table-cell">Vencimento</th>
                  <th className="p-4 font-medium">Status</th>
                  <th className="p-4 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                    </td>
                  </tr>
                ) : filteredHedges.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-muted-foreground">
                      Nenhum hedge encontrado
                    </td>
                  </tr>
                ) : (
                  filteredHedges.map((hedge) => {
                    const status = statusConfig[hedge.status] || { label: hedge.status, variant: "secondary" as const };
                    return (
                      <tr key={hedge.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                        <td className="p-4">
                          <span className="font-medium">{hedge.hedgeCode}</span>
                        </td>
                        <td className="p-4 hidden md:table-cell">
                          <div>
                            <div className="font-medium">{hedge.company?.razaoSocial || "-"}</div>
                            <div className="text-xs text-muted-foreground">
                              {hedge.company?.cnpj?.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5") || "-"}
                            </div>
                          </div>
                        </td>
                        <td className="p-4 hidden lg:table-cell">
                          <div>
                            <div className="font-medium">{hedge.invoice?.invoiceNumber || "-"}</div>
                            <div className="text-xs text-muted-foreground">{hedge.invoice?.supplierName || "-"}</div>
                          </div>
                        </td>
                        <td className="p-4 text-right">
                          <span className="font-semibold">{formatUsdc(hedge.hedgeAmountUsdc)}</span>
                        </td>
                        <td className="p-4 text-right hidden sm:table-cell">
                          <div className="font-semibold text-green-600">
                            R$ {parseFloat(hedge.lockedExchangeRate).toFixed(4)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            ref: {parseFloat(hedge.spotExchangeRate).toFixed(4)}
                          </div>
                        </td>
                        <td className="p-4 hidden sm:table-cell">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            {formatDate(hedge.dueDate)}
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge variant={status.variant}>{status.label}</Badge>
                        </td>
                        <td className="p-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/admin/hedges/${hedge.id}`)}>
                                <Eye className="h-4 w-4 mr-2" />
                                Ver Detalhes
                              </DropdownMenuItem>
                              {hedge.status === "PENDING_APPROVAL" && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => navigate(`/admin/hedges/${hedge.id}?action=approve`)}
                                    className="text-green-600"
                                  >
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Aprovar
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => navigate(`/admin/hedges/${hedge.id}?action=reject`)}
                                    className="text-red-600"
                                  >
                                    <XCircle className="h-4 w-4 mr-2" />
                                    Rejeitar
                                  </DropdownMenuItem>
                                </>
                              )}
                              {(hedge.status === "ACTIVE" || hedge.status === "PENDING_SETTLEMENT") && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => navigate(`/admin/hedges/${hedge.id}?action=default`)}
                                    className="text-red-600"
                                  >
                                    <AlertTriangle className="h-4 w-4 mr-2" />
                                    Marcar Inadimplente
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <div className="text-sm text-muted-foreground">
                Mostrando {((page - 1) * limit) + 1}-{Math.min(page * limit, total)} de {total}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Próximo
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
